import React, { useState, useMemo, useEffect, useCallback } from 'react'
import {
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  InlineGrid,
  InlineStack,
  Link,
  Text,
  Badge,
} from '@shopify/polaris'
import { useTranslation } from 'react-i18next'
import { numberFormater } from '../../../utils/formater'
import { useRedirect } from '../../../hooks'
import { PlanSlugs } from '../../../constants/enums'
import PlanDowngradeFeedbackForm from '../../../components/PlanDowngrafeFeedbackForm'
import { sendToHubspot } from '../../../utils/feedback'
import analytics, { MixPanelAnalytics } from '../../../utils/analytics'
import PriceEstimator from './PriceEstimator'
import {
  calculateOrderCountUsage,
  calculateEstimatedCharge,
  formatBillingPeriod,
  formatDate,
} from './accountHelpers'

/**
 * Account card component for ORDER_COUNT pricing strategy
 * Handles both trial and active subscription states with tiered pricing
 */
const OrderCountAccountCard = ({
  config,
  billingCycle,
  shopPlan,
  dashboardData,
  pricing,
  onOpenBilling,
  changePlanCB,
}) => {
  const { t } = useTranslation()
  const isTrialPeriod = billingCycle?.isTrialPeriod || false
  const [selectedOrderCount, setSelectedOrderCount] = useState(0)
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)
  const [selectedEstimatorPlan, setSelectedEstimatorPlan] = useState(null)
  const [selectedEstimatorOrderCount, setSelectedEstimatorOrderCount] = useState(0)
  // Find the correct free plan based on current pricing strategy
  const getFreePlanSlug = useCallback(() => {
    // Get available plans from pricing object
    const availablePlans = pricing?.plans || []
    const currentStrategy = shopPlan?.strategy || dashboardData?.config?.pricingStrategy
    
    // Find free plan that matches current strategy
    const freePlan = availablePlans.find(plan => 
      plan.isFree && plan.strategy === currentStrategy
    )
    
    if (!freePlan) {
      console.error('No free plan found for strategy:', currentStrategy)
      console.log('Available plans:', availablePlans)
      // Try to find any free plan as fallback
      const anyFreePlan = availablePlans.find(plan => plan.isFree)
      return anyFreePlan?.slug || PlanSlugs.FREE
    }
    
    // Return the slug of the found free plan
    return freePlan.slug
  }, [pricing?.plans, shopPlan?.strategy, dashboardData?.config?.pricingStrategy])
  
  // Handle feedback submission
  const feedbackHandler = async (values) => {
    console.log('Starting feedback submission...', values)
    try {
      // Send feedback to HubSpot
      const { error } = await sendToHubspot(values)
      if (error) {
        console.error('HubSpot submission error:', error)
        return {
          error,
        }
      }
      console.log('HubSpot submission successful')

      // Track downgrade event
      analytics.trackPlanInitEvent(MixPanelAnalytics.Actions.UPGRADE_INITIATED, {
        previousPlan: shopPlan?.plan,
        previousPrice: shopPlan?.price,
        updatedPlan: 'free',
        updatedPrice: 0,
        reason: values.reason,
        otherReason: values.otherReason,
        feedback: values.feedback,
        trial: isTrialPeriod
      })
      console.log('Analytics event tracked')
      
      // Proceed with plan change after feedback
      if (changePlanCB) {
        console.log('Calling changePlanCB with plan:', getFreePlanSlug())
        const url = await changePlanCB({ 
          plan: getFreePlanSlug(),
          promo: '',
          onAnnual: false
        })
        console.log('changePlanCB returned URL:', url)
        
        if (url) {
          // Don't close modal yet - let the redirect handle it
          console.log('Redirecting to:', url)
          
          // Force a full page reload by setting window.location
          // This ensures the dashboard reloads with the new plan
          window.location.href = url
          
          // Return without closing modal - the page will reload
          return { error: null, preventModalClose: true }
        } else {
          console.warn('No URL returned from changePlanCB, attempting fallback reload')
          // Fallback: reload current page if no URL returned
          window.location.reload()
          return { error: null, preventModalClose: true }
        }
      } else {
        console.error('changePlanCB is not defined')
        return { error: 'Plan change function not available' }
      }
    } catch (e) {
      console.error('feedbackHandler error:', e)
      return { error: e.message || 'An error occurred while changing plans' }
    }
  }

  // Calculate order count usage and projections
  const orderUsage = useMemo(() => 
    calculateOrderCountUsage(billingCycle, shopPlan),
    [billingCycle, shopPlan]
  )

  // Initialize slider with current order count
  useEffect(() => {
    if (orderUsage.currentOrderCount && selectedOrderCount === 0) {
      setSelectedOrderCount(orderUsage.currentOrderCount)
    }
  }, [orderUsage.currentOrderCount])
  // Get the current plan details - can come from shopPlan.planDetails or find it in pricing.plans
  const currentPlanDetails = shopPlan.planDetails

  useEffect(() => {
    if (orderUsage.projectedOrders && selectedEstimatorOrderCount === 0) {
      setSelectedEstimatorOrderCount(orderUsage.projectedOrders)
    }
  }, [orderUsage.projectedOrders])
  
  // Get paid ORDER_COUNT plans for free plan users
  const paidOrderCountPlans = useMemo(() => {
    if (!pricing?.plans) return []
    return pricing.plans.filter(plan => 
      !plan.isFree && 
      plan.strategy === (shopPlan?.strategy || dashboardData?.config?.pricingStrategy)
    )
  }, [pricing?.plans, shopPlan?.strategy, dashboardData?.config?.pricingStrategy])
  
  // Auto-select first plan if on free plan and no plan selected
  useEffect(() => {
    if (currentPlanDetails?.isFree && paidOrderCountPlans.length > 0 && !selectedEstimatorPlan) {
      setSelectedEstimatorPlan(paidOrderCountPlans[0])
    }
  }, [currentPlanDetails?.isFree, paidOrderCountPlans, selectedEstimatorPlan])
  
  // Determine which plan to use for estimation
  const planForEstimate = useMemo(() => {
    if (currentPlanDetails?.isFree && selectedEstimatorPlan) {
      return selectedEstimatorPlan
    }
    return shopPlan
  }, [currentPlanDetails?.isFree, selectedEstimatorPlan, shopPlan])
  
  // Get steps and max slider value
  const steps = useMemo(() => planForEstimate?.steps || [], [planForEstimate])
  const maxSliderValue = useMemo(() => 
    steps.length > 0 ? steps[steps.length - 1].max : 10000,
    [steps]
  )
  
  // Calculate estimate based on selected order count and appropriate plan
  const estimate = useMemo(() => {
    const basePrice = parseFloat(planForEstimate?.price || 0)
    return calculateEstimatedCharge(selectedOrderCount, steps, basePrice)
  }, [selectedOrderCount, steps, planForEstimate])
  
  // Handle plan selection for estimator
  const handleEstimatorPlanChange = useCallback((value) => {
    const selected = paidOrderCountPlans.find(p => {
      const planId = p.id ? String(p.id) : p.slug
      return planId === value
    })
    setSelectedEstimatorPlan(selected)
  }, [paidOrderCountPlans])

  // ============================================
  // Active Subscription Component
  // ============================================
  
  return (
    <>
      <Card>
      {isTrialPeriod && <Banner tone="info">
        <BlockStack gap="150">
          <Text variant="headingMd" as="h3">
            {t('SettingsPage.Sections.Account.Card.OrderCount.trialBannerTitle', {planName: currentPlanDetails?.name})}
          </Text>
          <Text>
            {t('SettingsPage.Sections.Account.Card.OrderCount.trialBannerEndingOn', {date: formatDate(billingCycle.endingOn)})}
          </Text>
          <Text variant="bodySm" tone="subdued">
            {t('SettingsPage.Sections.Account.Card.OrderCount.trialBannerDescription')}
          </Text>
        </BlockStack>
      </Banner>}
        {/* Plan Header */}
      <Box padding="300">
        <BlockStack gap="250">
          <InlineGrid columns="1fr auto" gap="300" alignItems="center">
            <BlockStack gap="100">
              <Text variant="headingMd" as="h3">
                {currentPlanDetails?.name} Plan
              </Text>
              <Text tone="subdued">
                {t('SettingsPage.Sections.Account.Card.OrderCount.description')}
              </Text>
            </BlockStack>
            
            {config.upgradeRequired ? (
              <Button
                url="/pricing"
                primary
              >
                {t('SettingsPage.Sections.Account.upgradeBtnCta')}
              </Button>
            ) : (
              <Link monochrome url="/pricing">
                {t('DefaultText.downgradeText')}
              </Link>
            )}
          </InlineGrid>
        </BlockStack>
      </Box>

      <Divider />

      {/* Current Usage and Projections */}
      <Box padding="300">
        <BlockStack gap="250">
          <Text variant="headingMd">{t('SettingsPage.Sections.Account.Card.OrderCount.currentBillingPeriod')}</Text>
          
          <InlineGrid columns={currentPlanDetails?.isFree ? "1fr" : "1fr 1fr"} gap="300">
            {/* Current Stats */}
            <BlockStack gap="200">
              {
                !currentPlanDetails?.isFree && <Text variant="bodyMd" fontWeight="semibold">{t('SettingsPage.Sections.Account.Card.OrderCount.currentStatus')}</Text>
              }
              
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text>{t('SettingsPage.Sections.Account.Card.OrderCount.ordersProcessed')}:</Text>
                  <Text fontWeight="semibold">
                    {numberFormater(orderUsage.currentOrderCount)}
                  </Text>
                </InlineStack>
                
                <InlineStack align="space-between">
                  <Text>{t('SettingsPage.Sections.Account.Card.OrderCount.currentTier')}:</Text>
                  <Badge tone={orderUsage.currentTier?.price === 0 ? "success" : "info"}>
                    {orderUsage.currentTier ? 
                      `${numberFormater(orderUsage.currentTier.min)}-${numberFormater(orderUsage.currentTier.max)} orders` 
                      : t('DefaultText.freeText')
                    }
                  </Badge>
                </InlineStack>
                
                <InlineStack align="space-between">
                  <Text>{t('SettingsPage.Sections.Account.Card.OrderCount.currentPrice')}:</Text>
                  {orderUsage.currentTier?.price === 0 ? 
                    <Badge tone="success">{t('DefaultText.freeText')}</Badge> : 
                    <Text fontWeight="semibold">
                      ${orderUsage.currentTier?.price || 0}
                    </Text>
                  }
                </InlineStack>
              </BlockStack>
            </BlockStack>

            {/* Projections */}
            {!currentPlanDetails?.isFree && <BlockStack gap="200">
              <Text variant="bodyMd" fontWeight="semibold">{t('SettingsPage.Sections.Account.Card.OrderCount.periodProjection')}</Text>

              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text>{t('SettingsPage.Sections.Account.Card.OrderCount.dailyAverage')}:</Text>
                  <Text fontWeight="semibold">
                    {numberFormater(orderUsage.dailyVelocity || 0)} {t('DefaultText.ordersPerDay')}
                  </Text>
                </InlineStack>
                
                <InlineStack align="space-between">
                  <Text>{t('SettingsPage.Sections.Account.Card.OrderCount.projectedTotal')}:</Text>
                  <Text fontWeight="semibold">
                    {numberFormater(orderUsage.projectedOrders)} {t('DefaultText.orders')}
                  </Text>
                </InlineStack>
                
                <InlineStack align="space-between">
                  <Text>{t('SettingsPage.Sections.Account.Card.OrderCount.estimatedCharge')}:</Text>
                  {orderUsage.estimatedCharge === 0 ? 
                    <Badge tone="success">{t('DefaultText.freeText')}</Badge> : 
                    <Text fontWeight="semibold">
                      ${orderUsage.estimatedCharge}
                    </Text>
                  }
                </InlineStack>
              </BlockStack>
            </BlockStack>}
          </InlineGrid>

          {/* Billing Period */}
          <Box paddingBlockStart="200">
            <InlineStack align="space-between">
              <Text tone="subdued">{t('SettingsPage.Sections.Account.Card.OrderCount.billingPeriod')}:</Text>
              <Text fontWeight="semibold">
                {formatBillingPeriod(billingCycle)}
              </Text>
            </InlineStack>
          </Box>
        </BlockStack>
      </Box>

      <Divider />

      {/* Price Estimator */}
      <Box padding="300">
        <PriceEstimator
          planForEstimate={planForEstimate}
          selectedOrderCount={selectedEstimatorOrderCount}
          estimate={estimate}
          maxSliderValue={maxSliderValue}
          isTrialPeriod={false}
          isFreePlan={currentPlanDetails?.isFree}
          availablePlans={paidOrderCountPlans}
          selectedPlan={selectedEstimatorPlan}
          onPlanChange={handleEstimatorPlanChange}
        />
      </Box>

      {/* Billing Management */}
      <Divider />
      <Box padding="300">
        <InlineGrid columns="1fr auto" gap="300" alignItems="center">
          <Text>
            {t('SettingsPage.Sections.Account.Card.Billing.description')}
          </Text>
          <Button onClick={onOpenBilling}>
            {t('SettingsPage.Sections.Account.Card.Billing.btnCta')}
          </Button>
        </InlineGrid>
      </Box>
      
      {/* Cancel Plan Section for Non-Trial */}
      {!currentPlanDetails?.isFree && (
        <>
          <Divider />
          <Box padding="300">
            <InlineStack align="space-between">
              <Text tone="subdued">
                Want to switch to the free plan?
              </Text>
              <Button
                variant="plain"
                onClick={() => setFeedbackModalOpen(true)}
              >
                Cancel Plan
              </Button>
            </InlineStack>
          </Box>
        </>
      )}
      </Card>
      
      {/* Feedback Modal */}
      <PlanDowngradeFeedbackForm
        submitCallback={feedbackHandler}
        open={feedbackModalOpen}
        setOpen={setFeedbackModalOpen}
      />
    </>
  )
}

export default OrderCountAccountCard